import type { SessionStatus } from "@hakili/app-shell";

/**
 * `P0-UIK-021`: the console's session, as a machine that needs no browser.
 *
 * **Why this is not inside a hook.** `P0-UIK-015` was ticked on screens nothing
 * ever mounted, and its stated test , *"login, refresh, logout and profile
 * update work end to end against staging"* , could not have passed. The lesson
 * is not "write more hooks": it is that behaviour buried in a component is
 * behaviour that can only be tested by rendering, and rendering was exactly
 * what nobody could do. So every transition lives here, in functions that take
 * a state and return a state, and `useSession` is the thin part.
 *
 * **The access token is held in memory and nowhere else.** Not
 * `localStorage`, not `sessionStorage`, not a readable cookie, not a URL. A
 * bearer token in storage survives the tab that earned it and is readable by
 * any script that reaches the origin; a token in a closure dies with the page.
 * The REFRESH authority lives in the `SameSite=Strict` cookie the API sets ,
 * which is what `A41`'s same-site hostnames were bought for, and why a reload
 * can recover a session that this module deliberately forgot.
 */

export interface Identity {
  /**
   * `P0-DLW-006`: the principal's own id, from `/me`'s `principal.id`.
   *
   * A team screen needs it for two things it must not guess , which row is the
   * caller's own, and the reassignment target 6.7 defaults to the manager , and
   * an app that matched on the EMAIL instead would break the day two people
   * share one address, which the schema permits nowhere and the world does.
   */
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  readonly organisation: string;
  /**
   * `P0-IDN-063`, `00-SPINE` 6.6: **which kind of organisation this person
   * belongs to**, verbatim from `identity.organisation_type`.
   *
   * Each surface admits one kind , the console Hakili's staff, dealer-web a
   * dealership's people, bank-web a bank's , and a gate cannot ask that
   * question of a NAME. The api has always answered it; nothing read it.
   */
  readonly organisationType: string;
  readonly view: string | null;
  /** Right key to level, exactly as `/me` answers it. */
  readonly rights: Readonly<Record<string, string>>;
  /**
   * The server's flag map, as `P0-PLT-034` answers it on `/me`.
   *
   * Read here rather than guessed because 4.7.3 evaluates flags SERVER side and
   * exposes them *"to the frontend through `/api/v1/me`"*. A build-time constant
   * would be a second opinion about a row a founder can change without a deploy.
   */
  readonly flags: Readonly<Record<string, boolean>>;
  /**
   * `P0-DLW-006`: the roles this principal may invite, from
   * `identity.roles.may_invite_roles` by way of `/me`.
   *
   * Empty for the many principals who may invite nobody. **An empty array is an
   * answer**: a screen offers the gesture when the array has an entry and hides
   * it when it does not, and never because of what it believes about a role.
   */
  readonly mayInviteRoles: readonly string[];
}

export type Session =
  | { readonly status: "pending" }
  | { readonly status: "anonymous"; readonly refusal?: string; readonly challenged?: boolean }
  | { readonly status: "authenticated"; readonly identity: Identity };

export const START: Session = { status: "pending" };

/**
 * What `AuthGate` reads. The shell knows three words; this machine knows why.
 *
 * **Renamed on the move into this package, round 189**: `queryClient.ts` next
 * door already exports a `statusOf`, and that one reads an HTTP status off a
 * failure. Two functions of one name in one surface is a barrel that answers
 * differently depending on import order, so the session's is
 * `sessionStatusOf` , the noun says which question it answers.
 */
export const sessionStatusOf = (session: Session): SessionStatus => session.status;

/**
 * **A challenged login is not a failure, and the difference is the whole
 * point.** `DECISION ADDED 06-02` makes two-factor mandatory for
 * `administration`: the login SUCCEEDS and every route except the enrolment
 * pair answers `403` with `details.reason = "totp_enrolment_required"`. The
 * form must then ask for a code rather than say "wrong password", which is what
 * a naive failure branch would show for a correct one.
 */
export const CHALLENGE = "totp_enrolment_required";

/**
 * The OTHER challenge, and the two are not interchangeable.
 *
 * `CHALLENGE` above is the ENROLMENT wall: the login succeeded, the user is an
 * `administration` who has never enrolled, and every route but the enrolment
 * pair answers `403` with that reason. This one is the login itself asking for
 * the second factor from a user who HAS enrolled , `domain/login.ts` returns
 * `forbidden` with `details.reason = "totp_code_required"` and the sentence
 * *"Un code de vérification est requis."*
 *
 * **Read as a `reason`, never as a `code`.** 4.5.5's `code` is a closed
 * registry (`packages/contracts`), and `forbidden` is what both of these are.
 * The distinction lives in `details.reason`, which is exactly why the envelope
 * carries one.
 */
export const CODE_DEMANDE = "totp_code_required";

export const challenged = (refusal: string): Session => ({
  status: "anonymous",
  refusal,
  challenged: true,
});

export const refused = (refusal: string): Session => ({ status: "anonymous", refusal });

export const signedIn = (identity: Identity): Session => ({ status: "authenticated", identity });

/**
 * The end of a session, however it ended.
 *
 * **Refresh failure lands HERE and never in a crash.** A silent refresh that
 * throws through the tree unmounts the shell and shows the error boundary,
 * which tells a person their console is broken when the truth is that their
 * session expired. `createQueryClient`'s `onSessionExpired` is fired at most
 * once for exactly this reason, and this is where it points.
 */
export const signedOut = (refusal?: string): Session =>
  refusal === undefined ? { status: "anonymous" } : { status: "anonymous", refusal };

/** Applies a profile edit to a live session, leaving any other state untouched. */
export const renamed = (session: Session, fullName: string): Session =>
  session.status === "authenticated"
    ? { status: "authenticated", identity: { ...session.identity, fullName } }
    : session;

/**
 * `/me`'s answer, narrowed to what the console renders.
 *
 * Written as a reader rather than a cast: the API's shape is the API's, and a
 * `as MeAnswer` here would turn a contract change into a blank screen instead
 * of a named failure.
 */
export function identityFrom(answer: unknown): Identity | null {
  if (typeof answer !== "object" || answer === null) return null;
  const body = answer as Record<string, unknown>;
  const profile = body["profile"];
  const organisation = body["organisation"];
  if (typeof profile !== "object" || profile === null) return null;

  const fields = profile as Record<string, unknown>;
  const fullName = fields["fullName"];
  const email = fields["email"];
  const role = body["role"];
  if (typeof fullName !== "string" || typeof email !== "string" || typeof role !== "string") {
    return null;
  }

  const view = body["view"];
  const viewId =
    typeof view === "object" &&
    view !== null &&
    typeof (view as Record<string, unknown>)["viewId"] === "string"
      ? ((view as Record<string, unknown>)["viewId"] as string)
      : null;

  const rights: Record<string, string> = {};
  const permissions = body["permissions"];
  if (typeof permissions === "object" && permissions !== null) {
    for (const [key, level] of Object.entries(permissions)) {
      if (typeof level === "string") rights[key] = level;
    }
  }

  const organisationName =
    typeof organisation === "object" &&
    organisation !== null &&
    typeof (organisation as Record<string, unknown>)["name"] === "string"
      ? ((organisation as Record<string, unknown>)["name"] as string)
      : "";

  const organisationType =
    typeof organisation === "object" &&
    organisation !== null &&
    typeof (organisation as Record<string, unknown>)["type"] === "string"
      ? ((organisation as Record<string, unknown>)["type"] as string)
      : "";

  const principal = body["principal"];
  const id =
    typeof principal === "object" &&
    principal !== null &&
    typeof (principal as Record<string, unknown>)["id"] === "string"
      ? ((principal as Record<string, unknown>)["id"] as string)
      : "";

  const invitable = body["mayInviteRoles"];
  const mayInviteRoles = Array.isArray(invitable)
    ? invitable.filter((role): role is string => typeof role === "string")
    : [];

  const flags: Record<string, boolean> = {};
  const serverFlags = body["flags"];
  if (typeof serverFlags === "object" && serverFlags !== null) {
    for (const [key, open] of Object.entries(serverFlags)) {
      if (typeof open === "boolean") flags[key] = open;
    }
  }

  return {
    id,
    fullName,
    email,
    role,
    organisation: organisationName,
    organisationType,
    view: viewId,
    rights,
    flags,
    mayInviteRoles,
  };
}

/**
 * The `reason` an envelope carries, read off `ApiError` rather than off a raw
 * body: `ApiClient` already parses 4.5.5's envelope into `status`, `code`,
 * `message` and `details`, and re-parsing it here would be a second reader of
 * one contract , the shape that drifts.
 */
export function reasonOf(failure: unknown): string | null {
  const details = (failure as { details?: unknown } | null)?.details;
  if (typeof details !== "object" || details === null) return null;
  const reason = (details as Record<string, unknown>)["reason"];
  return typeof reason === "string" ? reason : null;
}

/** The server's own French sentence, which is what a person should read. */
export function messageOf(failure: unknown, fallback: string): string {
  const message = (failure as { message?: unknown } | null)?.message;
  return typeof message === "string" && message !== "" ? message : fallback;
}
